import { useEffect, useState } from "react";
import {
  api,
  type Client,
  type ClientKvDefault,
  type KvConfig,
} from "../lib/api";
import { useClientContext } from "../lib/ClientContext";

export function SettingsClients() {
  const ctx = useClientContext();
  const [list, setList] = useState<Client[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState({
    id: "",
    name: "",
    contact_email: "",
    contact_name: "",
  });

  const load = async () => {
    setList(await api.clients.list({ includeArchived: true }));
    // Keep the shared (active-only) context in sync so other tabs' dropdowns
    // update immediately after create/archive/reactivate.
    await ctx.refresh();
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.id || !form.name) {
      setErr("Slug und Name sind pflicht.");
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await api.clients.create({
        id: form.id,
        name: form.name,
        contact_email: form.contact_email || null,
        contact_name: form.contact_name || null,
      });
      setMsg(`Mandant "${form.name}" angelegt.`);
      setForm({ id: "", name: "", contact_email: "", contact_name: "" });
      setCreateOpen(false);
      await load();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="toolbar">
        <button className="primary" onClick={() => setCreateOpen(!createOpen)}>
          {createOpen ? "Abbrechen" : "Mandant hinzufügen"}
        </button>
        <label className="field-inline">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <span>Archivierte anzeigen</span>
        </label>
        {msg && <span className="success-badge">✓ {msg}</span>}
      </div>

      {createOpen && (
        <div className="kv-form">
          {err && <div className="error">{err}</div>}
          <label className="field">
            <span>Slug (ID)</span>
            <input
              type="text"
              placeholder="acme"
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Name</span>
            <input
              type="text"
              placeholder="ACME GmbH"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Kontakt (Name)</span>
            <input
              type="text"
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Kontakt (E-Mail)</span>
            <input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            />
          </label>
          <button className="primary" disabled={busy} onClick={create}>
            {busy ? "Speichere…" : "Anlegen"}
          </button>
        </div>
      )}

      {list
        .filter((c) => showArchived || c.active)
        .map((c) => (
          <ClientCard
            key={c.id}
            client={c}
            expanded={expanded === c.id}
            toggleExpanded={() => setExpanded(expanded === c.id ? null : c.id)}
            onChanged={load}
          />
        ))}
    </div>
  );
}

function ClientCard(props: {
  client: Client;
  expanded: boolean;
  toggleExpanded: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const { client: c } = props;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: c.name,
    contact_name: c.contact_name ?? "",
    contact_email: c.contact_email ?? "",
    notes: c.notes ?? "",
  });

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.clients.patch(c.id, {
        name: form.name,
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        notes: form.notes || null,
      } as any);
      setEditing(false);
      await props.onChanged();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleArchive = async () => {
    const target = c.active ? false : true;
    const verb = target ? "reaktivieren" : "archivieren";
    if (!confirm(`Mandant "${c.name}" ${verb}?`)) return;
    setBusy(true);
    try {
      await api.clients.patch(c.id, { active: target } as any);
      await props.onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`kv-card ${editing ? "editing" : ""}`}>
      <div className="kv-header">
        <strong>{c.name}</strong>
        <span className="muted" style={{ marginLeft: 8 }}>({c.id})</span>
        {!c.active && <span className="badge">archiviert</span>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="link" onClick={() => setEditing(!editing)} disabled={busy}>
            {editing ? "Abbrechen" : "Ändern"}
          </button>
          <button className="link" onClick={toggleArchive} disabled={busy}>
            {c.active ? "Archivieren" : "Reaktivieren"}
          </button>
          <button className="link" onClick={props.toggleExpanded}>
            {props.expanded ? "KV-Verlauf schließen" : "KV-Verlauf"}
          </button>
        </div>
      </div>
      {editing ? (
        <div className="kv-form">
          {err && <div className="error">{err}</div>}
          <label className="field">
            <span>Name</span>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="field">
            <span>Kontakt (Name)</span>
            <input type="text" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
          </label>
          <label className="field">
            <span>Kontakt (E-Mail)</span>
            <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          </label>
          <label className="field">
            <span>Notizen</span>
            <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </label>
          <button className="primary" disabled={busy} onClick={save}>
            {busy ? "Speichere…" : "Speichern"}
          </button>
        </div>
      ) : (
        <dl className="kv-grid">
          {c.contact_name && (<><dt>Kontakt</dt><dd>{c.contact_name}</dd></>)}
          {c.contact_email && (<><dt>E-Mail</dt><dd>{c.contact_email}</dd></>)}
          {c.notes && (<><dt>Notizen</dt><dd>{c.notes}</dd></>)}
        </dl>
      )}
      {props.expanded && <ClientKvDefaultsTimeline clientId={c.id} />}
    </div>
  );
}

function ClientKvDefaultsTimeline({ clientId }: { clientId: string }) {
  const [rows, setRows] = useState<ClientKvDefault[]>([]);
  const [kvList, setKvList] = useState<KvConfig[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    kv_config_id: "",
    valid_from: "",
    note: "",
  });

  const load = async () => {
    const [d, k] = await Promise.all([
      api.clientKvDefaults.list(clientId),
      api.kvConfigs.list(),
    ]);
    setRows(d);
    setKvList(k);
    if (!form.kv_config_id && k.length > 0) {
      setForm((f) => ({ ...f, kv_config_id: k[0]!.id }));
    }
  };

  useEffect(() => {
    load();
  }, [clientId]);

  const add = async () => {
    if (!form.kv_config_id || !form.valid_from) {
      setErr("KV und gültig-ab-Datum sind pflicht.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.clientKvDefaults.add({
        client_id: clientId,
        kv_config_id: form.kv_config_id,
        valid_from: form.valid_from,
        note: form.note || null,
      });
      setForm({ kv_config_id: kvList[0]?.id ?? "", valid_from: "", note: "" });
      await load();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (id: string) => {
    if (!confirm("Wirklich löschen?")) return;
    await api.clientKvDefaults.delete(id);
    await load();
  };

  const kvName = (id: string) =>
    kvList.find((k) => k.id === id)?.name ?? id;

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
      {rows.length === 0 ? (
        <div className="muted">Kein Standard-KV hinterlegt.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Gültig ab</th>
              <th>Gültig bis</th>
              <th>KV</th>
              <th>Notiz</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.valid_from}</td>
                <td>{r.valid_until ?? <span className="muted">offen</span>}</td>
                <td>{kvName(r.kv_config_id)}</td>
                <td>{r.note ?? <span className="muted">—</span>}</td>
                <td>
                  <button className="link" onClick={() => del(r.id)}>Löschen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="kv-form">
        <h4>Standard-KV ab Stichtag setzen</h4>
        {err && <div className="error">{err}</div>}
        <label className="field">
          <span>KV</span>
          <select
            value={form.kv_config_id}
            onChange={(e) => setForm({ ...form, kv_config_id: e.target.value })}
          >
            {kvList.map((k) => (
              <option key={k.id} value={k.id}>{k.name}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Gültig ab</span>
          <input
            type="date"
            value={form.valid_from}
            onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Notiz</span>
          <input
            type="text"
            placeholder="z. B. Jahreswechsel UBIT 2026"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
        </label>
        <button className="primary" disabled={busy} onClick={add}>
          {busy ? "Speichere…" : "Setzen"}
        </button>
        <p className="muted small">
          Der bisher offene Standard wird automatisch mit dem Tag davor geschlossen.
        </p>
      </div>
    </div>
  );
}
